/**
 * Utilitário para tratamento padronizado de erros da API
 */

/**
 * Trata erros de requisições da API e retorna mensagem amigável
 * @param {Error} error - Erro capturado do catch
 * @param {string} operacao - Nome da operação (ex: 'adicionar', 'atualizar', 'excluir')
 * @param {string} entidade - Nome da entidade (ex: 'escola', 'pavimento', 'ambiente')
 * @returns {string} - Mensagem de erro formatada para o usuário
 */
export const tratarErroAPI = (error, operacao = 'realizar a operação', entidade = 'registro') => {
  // Log detalhado no console para debug
  console.error(`❌ Erro ao ${operacao} ${entidade}:`, {
    status: error.response?.status,
    mensagem: error.message,
    dadosBackend: error.response?.data,
    erroCompleto: error
  });

  const status = error.response?.status;
  const mensagemBackend = error.response?.data?.message || error.response?.data?.error;

  // Mensagens específicas por código HTTP
  switch (status) {
    case 400:
      // Dados inválidos
      // Tratativa específica para enums do PostgreSQL
      if (mensagemBackend?.toLowerCase?.().includes('invalid input value for enum')) {
        const valorInvalidoMatch = /enum\s+([\w_]+).*?:\s*"?([\w_ãáàâêéíóôõúç\- ]+)"?/i.exec(mensagemBackend);
        const enumName = valorInvalidoMatch?.[1];
        const valorEnviado = valorInvalidoMatch?.[2];
        return `Erro: Valor inválido${enumName ? ` para enum ${enumName}` : ''}.` +
               `${valorEnviado ? ` Valor enviado: "${valorEnviado}".` : ''} ` +
               `Verifique os valores permitidos no banco ou ajuste o mapeamento no formulário.`;
      }
      if (mensagemBackend?.includes('foreign key')) {
        // Detecta qual tabela/campo está faltando
        if (mensagemBackend.includes('id_escola')) {
          return `Erro: Escola não encontrada. Verifique se a escola selecionada existe no sistema.`;
        }
        if (mensagemBackend.includes('id_projeto')) {
          return `Erro: Projeto não encontrado. Verifique se o projeto selecionado existe no sistema.`;
        }
        if (mensagemBackend.includes('id_municipio')) {
          return `Erro: Município não encontrado. Verifique se o município selecionado existe no sistema.`;
        }
        if (mensagemBackend.includes('id_pavimento')) {
          return `Erro: Pavimento não encontrado. Verifique se o pavimento selecionado existe no sistema.`;
        }
        if (mensagemBackend.includes('id_ambiente')) {
          return `Erro: Ambiente não encontrado. Verifique se o ambiente selecionado existe no sistema.`;
        }
        if (mensagemBackend.includes('id_tipo')) {
          return `Erro: Tipo selecionado não existe. Verifique os tipos disponíveis no sistema.`;
        }
        if (mensagemBackend.includes('id_gerencia')) {
          return `Erro: Gerência não encontrada. Verifique se a gerência selecionada existe no sistema.`;
        }
        if (mensagemBackend.includes('id_superintendencia')) {
          return `Erro: Superintendência não encontrada. Verifique se a superintendência existe no sistema.`;
        }
        if (mensagemBackend.includes('sigla_estado')) {
          return `Erro: Estado não encontrado. Verifique se o estado selecionado existe no sistema.`;
        }
        if (mensagemBackend.includes('id_regional')) {
          return `Erro: Regional não encontrada. Verifique se a regional selecionada existe no sistema.`;
        }
        // Mensagem genérica para outros casos
        return `Erro: Registro relacionado não existe. Verifique se os dados vinculados existem no sistema.`;
      }
      if (mensagemBackend?.includes('violates')) {
        if (mensagemBackend.includes('uq_codigo_tipo_ambiente')) {
          return `Erro: O código informado já está em uso por outro tipo de ambiente.`;
        }
        return `Erro: ${mensagemBackend}`;
      }
      if (mensagemBackend?.includes('obrigatório') || mensagemBackend?.includes('required')) {
        return `Erro: Campos obrigatórios não preenchidos. ${mensagemBackend}`;
      }
      return `Erro: Dados inválidos. ${mensagemBackend || 'Verifique os campos preenchidos e tente novamente.'}`;

    case 404:
      // Não encontrado - detecta automaticamente qual registro
      const entidadeFormatada = entidade.charAt(0).toUpperCase() + entidade.slice(1);

      // Se a mensagem do backend já especifica o que não foi encontrado, use ela
      if (mensagemBackend) {
        if (mensagemBackend.includes('Escola')) return `Erro: Escola não encontrada. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Pavimento')) return `Erro: Pavimento não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Ambiente')) return `Erro: Ambiente não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Município')) return `Erro: Município não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Projeto')) return `Erro: Projeto não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Aluno')) return `Erro: Aluno não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Usuário')) return `Erro: Usuário não encontrado. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Desenho')) return `Erro: Desenho não encontrado. O arquivo pode ter sido excluído.`;
        if (mensagemBackend.includes('Imagem')) return `Erro: Imagem não encontrada. O arquivo pode ter sido excluído.`;
        if (mensagemBackend.includes('Vistoria')) return `Erro: Vistoria não encontrada. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Manutenção')) return `Erro: Manutenção não encontrada. O registro pode ter sido excluído.`;
        if (mensagemBackend.includes('Solicitação')) return `Erro: Solicitação não encontrada. O registro pode ter sido excluído.`;
        return `Erro: ${mensagemBackend}`;
      }

      // Usa a entidade passada como parâmetro (do manager)
      return `Erro: ${entidadeFormatada} não encontrado(a). O registro pode ter sido excluído ou você não tem acesso.`;

    case 409:
      // Conflito (duplicação)
      if (mensagemBackend?.includes('já existe')) {
        return `Erro: ${mensagemBackend}`;
      }
      return `Erro: Registro duplicado. Este ${entidade} já existe no sistema.`;

    case 500:
      // Erro no servidor
      return `Erro no servidor. Tente novamente mais tarde.`;

    case 401:
      // Não autorizado
      return `Erro: Sessão expirada. Por favor, faça login novamente.`;

    case 403:
      // Sem permissão
      if (mensagemBackend) {
        return `Erro de permissão: ${mensagemBackend}`;
      }
      return `Erro: Você não tem permissão para ${operacao} este ${entidade}.`;

    default:
      // Erro genérico
      if (mensagemBackend) {
        return `Erro: ${mensagemBackend}`;
      }
      return `Erro ao ${operacao} ${entidade}: ${error.message || 'Erro desconhecido. Tente novamente.'}`;
  }
};

/**
 * Loga detalhes da requisição no console
 * @param {string} operacao - Tipo da operação (ex: 'Adicionar', 'Atualizar', 'Excluir')
 * @param {string} entidade - Nome da entidade
 * @param {object} dados - Dados sendo enviados
 */
export const logarRequisicao = (operacao, entidade, dados) => {
  console.log(`📤 ${operacao} ${entidade}:`, dados);
};

/**
 * Loga sucesso da operação
 * @param {string} operacao - Tipo da operação
 * @param {string} entidade - Nome da entidade
 * @param {object} resposta - Resposta do servidor
 */
export const logarSucesso = (operacao, entidade, resposta) => {
  console.log(`✅ ${operacao} ${entidade} - Sucesso:`, resposta);
};
